import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { requiresEmailVerification, useAuth } from '../contexts/AuthContext';
import { FcGoogle } from 'react-icons/fc';
import { useTranslation } from 'react-i18next';
import { resolvePostAuthPath } from '../lib/resolvePostAuthPath';
import { savePendingAuthJourney } from '../lib/authJourney';
import { ChevronDown, ShieldCheck } from 'lucide-react';
import {
  getAccessCampaignErrorTranslationKey,
  redeemAccessCampaignToken,
} from '../services/accessCampaigns';
import {
  trackSignupMethodSelected,
  trackSignupStarted,
  trackSignupViewed,
} from '../services/analyticsService';

function extractBlockingAuthErrorMessage(error) {
  const raw = String(error?.message || '');
  const markers = ['AUTH_RATE_LIMIT_SIGNUP:', 'AUTH_RATE_LIMIT_SIGNIN:'];

  for (const marker of markers) {
    const markerIndex = raw.indexOf(marker);
    if (markerIndex === -1) continue;

    return raw.slice(markerIndex + marker.length).trim();
  }

  return '';
}

function getAuthErrorMessage(error, t) {
  const code = error?.code;
  const blockingMessage = extractBlockingAuthErrorMessage(error);

  if (blockingMessage) {
    return blockingMessage;
  }

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return t('auth:errors.invalidCredentials');
    case 'auth/email-already-in-use':
      return t('auth:errors.emailInUse');
    case 'auth/weak-password':
      return t('auth:errors.weakPassword');
    case 'auth/invalid-email':
      return t('auth:errors.invalidEmail');
    case 'auth/too-many-requests':
      return t('auth:errors.tooManyRequests');
    case 'auth/popup-closed-by-user':
      return t('auth:errors.popupClosed');
    case 'auth/network-request-failed':
      return t('auth:errors.network');
    case 'auth/user-disabled':
      return t('auth:errors.userDisabled');
    default:
      return error?.message || t('auth:errors.unknown');
  }
}

function releaseDialogLock() {
  if (typeof document === 'undefined') return;
  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
  document.documentElement.style.pointerEvents = '';
  document.documentElement.style.overflow = '';
}

function waitForDialogClosePaint() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0);
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });
}

function redirectToPath(path, navigate) {
  const targetPath = typeof path === 'string' && path.trim() ? path : '/';

  if (/^(?:[a-z]+:)?\/\//i.test(targetPath)) {
    if (typeof window !== 'undefined' && window.location?.assign) {
      window.location.assign(targetPath);
      return;
    }
  }

  navigate(targetPath, { replace: true });
}

export default function AuthModal({
  isOpen,
  onClose,
  mode = 'login',
  onSuccess,
  redirectTo,
  analyticsSource = 'auth_modal',
  analyticsLanguage,
  signupContext,
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentMode, setCurrentMode] = useState(mode);
  const viewedSignupKeyRef = useRef(null);

  const { login, signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  async function maybeRedeemAccessCode() {
    if (currentMode !== 'signup' || !accessCode.trim()) {
      return null;
    }

    try {
      await redeemAccessCampaignToken(accessCode);
      return null;
    } catch (redeemError) {
      return t(getAccessCampaignErrorTranslationKey(redeemError));
    }
  }

  // Add this useEffect to sync the internal state with the prop
  useEffect(() => {
    setCurrentMode(mode);
  }, [mode]);

  useEffect(() => {
    if (!isOpen || currentMode !== 'signup' || typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);
    const queryAccessCode = searchParams.get('access_code') || searchParams.get('accessCode');
    if (queryAccessCode) {
      setAccessCode(queryAccessCode);
      setShowAccessCode(true);
    }
  }, [currentMode, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      viewedSignupKeyRef.current = null;
      return;
    }

    if (currentMode !== 'signup' || viewedSignupKeyRef.current === analyticsSource) return;

    viewedSignupKeyRef.current = analyticsSource;
    trackSignupViewed({
      source: analyticsSource,
      landingPage: typeof window !== 'undefined' ? window.location.pathname : undefined,
      language: analyticsLanguage,
    });
  }, [analyticsLanguage, analyticsSource, currentMode, isOpen]);

  const signupAnalyticsContext = () => ({
    source: analyticsSource,
    landingPage: typeof window !== 'undefined' ? window.location.pathname : undefined,
    language: analyticsLanguage,
  });

  // Defensive cleanup for rare dialog lock leftovers after route transitions
  useEffect(() => {
    let frameId = null;

    if (!isOpen && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      frameId = window.requestAnimationFrame(() => {
        releaseDialogLock();
      });
    }

    return () => {
      if (frameId !== null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(frameId);
      }
      releaseDialogLock();
    };
  }, [isOpen]);

  const closeModalBeforeRedirect = async () => {
    onClose?.();
    await waitForDialogClosePaint();
    releaseDialogLock();
  };

  // Helper to check if user needs onboarding and navigate accordingly
  const navigateAfterAuth = async (user) => {
    console.log('navigateAfterAuth called with user:', user?.uid);

    // If there's an onSuccess callback, let the caller handle navigation
    if (onSuccess) {
      return;
    }

    // If explicit redirectTo is provided, use it
    if (redirectTo) {
      redirectToPath(redirectTo, navigate);
      return;
    }

    // Safety check - if no user, go to onboarding
    if (!user) {
      console.warn('No user provided to navigateAfterAuth, defaulting to onboarding');
      redirectToPath('/onboarding', navigate);
      return;
    }

    redirectToPath(await resolvePostAuthPath(user), navigate);
  };

  const rememberSignupJourney = () => {
    if (currentMode !== 'signup' || typeof window === 'undefined') return null;

    return savePendingAuthJourney(window.sessionStorage, {
      source: analyticsSource,
      language: analyticsLanguage,
      ...signupContext,
      accessCode,
      returnTo: redirectTo || signupContext?.returnTo,
    });
  };


  async function handleSubmit(e) {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      
      if (currentMode === 'login') {
        console.log('Attempting login...');
        const userCredential = await login(email, password);
        console.log('Login successful, checking onboarding status...');
        await closeModalBeforeRedirect();
        if (requiresEmailVerification(userCredential.user)) {
          redirectToPath('/verify-email', navigate);
          return;
        }
        if (onSuccess) {
          await onSuccess('password');
        } else {
          await navigateAfterAuth(userCredential.user);
        }
      } else {
        console.log('Attempting signup...');
        trackSignupMethodSelected('email', signupAnalyticsContext());
        trackSignupStarted('email', signupAnalyticsContext());
        rememberSignupJourney();
        const userCredential = await signup(email, password);
        const verificationRequired = requiresEmailVerification(userCredential.user);
        const redeemErrorMessage = verificationRequired ? null : await maybeRedeemAccessCode();
        console.log('Signup successful, redirecting to verify-email...');
        await closeModalBeforeRedirect();
        if (verificationRequired) {
          if (redeemErrorMessage && typeof window !== 'undefined') {
            window.alert(redeemErrorMessage);
          }
          redirectToPath('/verify-email', navigate);
          return;
        }
        if (onSuccess) {
          await onSuccess('password');
        } else {
          await navigateAfterAuth(userCredential.user);
        }
        if (redeemErrorMessage && typeof window !== 'undefined') {
          window.alert(redeemErrorMessage);
        }
      }

      setEmail('');
      setPassword('');
      setAccessCode('');
    } catch (error) {
      setError(getAuthErrorMessage(error, t));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleLogin() {
    try {
      setError('');
      setLoading(true);
      console.log('Attempting Google login...');
      if (currentMode === 'signup') {
        trackSignupMethodSelected('google', signupAnalyticsContext());
        trackSignupStarted('google', signupAnalyticsContext());
      }
      const pendingJourney = rememberSignupJourney();
      const result = await loginWithGoogle();
      const redeemErrorMessage = await maybeRedeemAccessCode();
      if (pendingJourney && accessCode.trim() && !redeemErrorMessage && typeof window !== 'undefined') {
        savePendingAuthJourney(window.sessionStorage, {
          ...pendingJourney,
          accessCode: '',
        });
      }
      console.log('Google login successful, checking onboarding status...');
      await closeModalBeforeRedirect();
      if (onSuccess) {
        await onSuccess('google');
      } else {
        await navigateAfterAuth(result.user);
      }
      if (redeemErrorMessage && typeof window !== 'undefined') {
        window.alert(redeemErrorMessage);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setError(getAuthErrorMessage(error, t));
    } finally {
      setLoading(false);
    }
  }

  function switchMode() {
    setCurrentMode(currentMode === 'login' ? 'signup' : 'login');
    setError('');
    setAccessCode('');
    setShowAccessCode(false);
  }

  const isSignup = currentMode === 'signup';
  const isPostDemoSignup = isSignup && analyticsSource === 'homepage_demo_completion';
  const signupTitle = isPostDemoSignup
    ? t('auth:modal.saveProgress', { defaultValue: 'Save your progress' })
    : t('auth:modal.createAccount');

  const googleButton = (
    <Button
      type="button"
      variant="outline"
      onClick={handleGoogleLogin}
      disabled={loading}
      className="min-h-12 w-full border-stone-300 bg-white text-[15px] font-semibold text-stone-900 shadow-[0_8px_24px_-18px_rgba(28,25,23,0.55)] hover:border-stone-900 hover:bg-stone-50"
    >
      <FcGoogle className="mr-2 h-5 w-5" />
      {isSignup
        ? t('auth:modal.googleSignup', { defaultValue: 'Continue with Google' })
        : t('auth:modal.google')}
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        overlayClassName="auth-modal-overlay"
        className="auth-modal-content !max-w-[390px] border-stone-200 bg-[#FDFBF7] p-6 sm:p-7"
      >
        <DialogHeader>
          <DialogTitle
            className="text-center text-[1.9rem] leading-tight text-stone-950"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {currentMode === 'login' ? t('auth:modal.welcomeBack') : signupTitle}
          </DialogTitle>
          {isSignup ? (
            <p className="mx-auto mt-2 max-w-[30ch] text-center text-sm leading-6 text-stone-600">
              {t('auth:modal.signupValue', { defaultValue: 'Keep your result and continue learning.' })}
            </p>
          ) : null}
        </DialogHeader>

        {isSignup ? googleButton : null}

        {isSignup ? (
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center text-[11px] font-semibold uppercase tracking-[0.14em]">
              <span className="bg-[#FDFBF7] px-3 text-stone-500">
                {t('auth:modal.emailSignup', { defaultValue: 'Sign up with email' })}
              </span>
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="email">{t('auth:modal.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 focus-visible:ring-[#D63AB4]/30 focus-visible:border-[#D63AB4]"
            />
          </div>

          <div>
            <Label htmlFor="password">{t('auth:modal.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 focus-visible:ring-[#D63AB4]/30 focus-visible:border-[#D63AB4]"
            />
          </div>

          {isSignup ? (
            <div>
              <button
                type="button"
                onClick={() => setShowAccessCode((visible) => !visible)}
                aria-expanded={showAccessCode}
                className="inline-flex min-h-9 items-center gap-1.5 text-sm font-semibold text-stone-600 hover:text-stone-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
              >
                {t('auth:modal.haveAccessCode', { defaultValue: 'Have an access code?' })}
                <ChevronDown
                  size={15}
                  aria-hidden="true"
                  className={`transition-transform ${showAccessCode ? 'rotate-180' : ''}`}
                />
              </button>
              {showAccessCode ? (
                <div className="mt-2">
                  <Label htmlFor="accessCode">{t('auth:modal.accessCodeLabel')}</Label>
                  <Input
                    id="accessCode"
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="SPAN-7KQ9"
                    className="mt-1 focus-visible:border-[#D63AB4] focus-visible:ring-[#D63AB4]/30"
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {isSignup ? (
            <div className="flex gap-2 rounded-xl border border-stone-200 bg-white/80 px-3 py-2.5 text-xs leading-5 text-stone-600">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#B2288E]" aria-hidden="true" />
              <span>
                {t('auth:modal.emailVerificationNote', {
                  defaultValue: 'Email signups must be verified before learning access is enabled.',
                })}
              </span>
            </div>
          ) : null}
          
          <Button
            type="submit"
            className="min-h-12 w-full bg-stone-950 text-[15px] font-semibold text-white hover:bg-stone-800"
            disabled={loading}
          >
            {loading
              ? t('auth:modal.loading')
              : (currentMode === 'login'
                ? t('auth:modal.signIn')
                : t('auth:modal.emailSignup', { defaultValue: 'Sign up with email' }))}
          </Button>
        </form>

        {!isSignup ? (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#FDFBF7] px-2 text-stone-500">{t('auth:modal.orContinueWith')}</span>
              </div>
            </div>
            {googleButton}
          </>
        ) : null}
        
        <div className="text-center text-sm">
          {currentMode === 'login' ? (
            <>
              {t('auth:modal.noAccount')}{' '}
              <button
                type="button"
                onClick={switchMode}
                className="hover:underline"
                style={{ color: '#D63AB4' }}
              >
                {t('auth:modal.signUp')}
              </button>
            </>
          ) : (
            <>
              {t('auth:modal.hasAccount')}{' '}
              <button
                type="button"
                onClick={switchMode}
                className="hover:underline"
                style={{ color: '#D63AB4' }}
              >
                {t('auth:modal.signInLink')}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
